/* eslint-disable react-refresh/only-export-components */
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import type { ChildrenType } from "@/types/types";
import type {
    AddressType,
    CartType,
    FavoriteType,
    PointUserType,
    UserType,
} from "@/types/user-type";
import { UserContext } from "../contexts/user-context";
import { getDataUser } from "@/apis/user-api";
import { AuthContext } from "@/contexts/auth-context";
import type { FarmUserType } from "@/types/farm-type";
import { FarmContext } from "@/contexts/farm-context";

export function AuthProvider({ children }: ChildrenType) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        return !!localStorage.getItem("user");
    });
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
    const [point, setPoint] = useState<PointUserType | null>(null);
    const [vUsage, setVUsage] = useState<number[] | null>(null);
    const [address, setAddress] = useState<AddressType[] | null>(null);
    const [cart, setCart] = useState<CartType[] | null>(null);
    const [favorite, setFavorite] = useState<FavoriteType[] | null>(null);
    const [farm, setFarm] = useState<FarmUserType[] | null>(null);

    const [user, setUser] = useState<UserType | null>(() => {
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                return JSON.parse(userData);
            } catch (e) {
                void e;
                localStorage.removeItem("user");
                return null;
            }
        }
        return null;
    });

    const clearUserContext = useCallback(() => {
        setPoint(null);
        setVUsage(null);
        setAddress(null);
        setCart(null);
        setFavorite(null);
    }, []);

    const fetchUserContext = useCallback(async () => {
        try {
            const response = await getDataUser();
            const data = response.data;
            setPoint(data.point);
            setVUsage(data.vUsage);
            setAddress(data.address);
            setCart(data.cart);
            setFavorite(data.favorite);
        } catch (error) {
            console.error("Gagal mengambil data context user", error);
        }
    }, []);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;

        if (isAuthenticated && user?.emailVerifiedAt) {
            hasFetched.current = true;
            // eslint-disable-next-line react-hooks/set-state-in-effect
            fetchUserContext();
        }
    }, [isAuthenticated, user, fetchUserContext]);

    // Handle session-expired dari API interceptor
    useEffect(() => {
        const handleSessionExpired = () => {
            setIsAuthenticated(false);
            setUser(null);
            clearUserContext();
            hasFetched.current = false;
        };

        window.addEventListener("session-expired", handleSessionExpired);
        return () =>
            window.removeEventListener("session-expired", handleSessionExpired);
    }, [clearUserContext]);

    const login = (userData: UserType) => {
        localStorage.setItem("user", JSON.stringify(userData));
        setIsAuthenticated(true);
        setUser(userData);

        // Ambil data jika user sudah verifikasi email
        if (userData.emailVerifiedAt) {
            hasFetched.current = true;
            fetchUserContext();
        }
    };

    const logout = () => {
        setIsLoggingOut(true);
        localStorage.removeItem("user");
        setIsAuthenticated(false);
        setUser(null);
        clearUserContext();
        hasFetched.current = false;
        setTimeout(() => setIsLoggingOut(false), 100);
    };

    return (
        <AuthContext.Provider
            value={{ isAuthenticated, isLoggingOut, user, login, logout }}>
            <UserContext.Provider
                value={{
                    point,
                    setPoint,
                    vUsage,
                    setVUsage,
                    address,
                    setAddress,
                    cart,
                    setCart,
                    favorite,
                    setFavorite,
                }}>
                <FarmContext.Provider value={{ farm, setFarm }}>
                    {children}
                </FarmContext.Provider>
            </UserContext.Provider>
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
